import React, { useState, useRef, useEffect } from 'react';
import { 
  Camera, CameraOff, RefreshCw, Volume2, CheckCircle2, 
  RotateCcw, Play, Pause, AlertCircle, Sparkles, Smile, Shield 
} from 'lucide-react';

interface PhonemeGuide {
  phoneme: string;
  name: string;
  classification: string;
  tonguePlacement: string;
  lipShape: string;
  instructions: string;
  targetMouthSvg: string;
}

const PHONEME_GUIDES: Record<string, PhonemeGuide> = {
  '/r/': {
    phoneme: '/r/',
    name: 'Retroflex / Alveolar Liquid',
    classification: 'Post-Alveolar Approximant',
    tonguePlacement: 'Elevate tongue tip and blade toward alveolar ridge; sides of tongue curl against upper molars.',
    lipShape: 'Slightly rounded and relaxed, teeth slightly parted.',
    instructions: 'Curl the tip of your tongue up and back without touching the roof of your mouth. Keep sides firm against back teeth.',
    targetMouthSvg: 'M 15 45 C 25 30, 75 30, 85 45 C 75 55, 25 55, 15 45 Z'
  },
  '/s/': {
    phoneme: '/s/',
    name: 'Alveolar Sibilant Fricative',
    classification: 'Voiceless Lingua-Alveolar',
    tonguePlacement: 'Tongue tip near upper incisors forming a narrow central groove; sides sealed against upper gum line.',
    lipShape: 'Spread into a slight smile; teeth almost touching.',
    instructions: 'Form a gentle smile. Direct a cool, sharp stream of air through the center groove of your teeth.',
    targetMouthSvg: 'M 10 50 C 30 40, 70 40, 90 50 C 70 55, 30 55, 10 50 Z'
  },
  '/l/': {
    phoneme: '/l/',
    name: 'Lateral Alveolar Liquid',
    classification: 'Voiced Alveolar Lateral',
    tonguePlacement: 'Tongue tip pressed firmly against alveolar ridge behind front teeth; air escapes around the sides.',
    lipShape: 'Neutral to slightly parted.',
    instructions: 'Touch your tongue tip to the spot right behind your top front teeth. Let sound flow out around the sides.',
    targetMouthSvg: 'M 20 48 C 35 35, 65 35, 80 48 C 65 58, 35 58, 20 48 Z'
  },
  '/th/': {
    phoneme: '/th/',
    name: 'Interdental Fricative',
    classification: 'Lingua-Dental Fricative',
    tonguePlacement: 'Tongue tip gently protrudes between upper and lower incisors.',
    lipShape: 'Relaxed and parted.',
    instructions: 'Place the tip of your tongue lightly between your teeth. Gently blow air out across the tongue.',
    targetMouthSvg: 'M 18 45 C 30 38, 70 38, 82 45 C 70 52, 30 52, 18 45 Z'
  },
  '/p/': {
    phoneme: '/p/',
    name: 'Bilabial Plosive Stop',
    classification: 'Voiceless Bilabial Stop',
    tonguePlacement: 'Resting flat on the floor of the mouth.',
    lipShape: 'Lips pressed firmly together to build intraoral pressure, then released suddenly.',
    instructions: 'Press both lips tight together. Build a pocket of air, then pop lips open with a puff of air.',
    targetMouthSvg: 'M 12 50 C 35 48, 65 48, 88 50 C 65 52, 35 52, 12 50 Z'
  }
};

const ORAL_MOTOR_DRILLS = [
  { id: 'lip_pucker', title: 'Lip Pucker to Smile', target: 'Bilabial strength & range of motion', reps: 10 },
  { id: 'tongue_lateral', title: 'Lingual Lateralization', target: 'Tongue tip elevation & side sweep', reps: 10 },
  { id: 'cheek_puff', title: 'Velopharyngeal Cheek Puff', target: 'Intraoral air pressure & seal', reps: 8 }
];

export const DigitalArticulationMirror: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isMirrorFlipped, setIsMirrorFlipped] = useState(true);
  const [selectedPhoneme, setSelectedPhoneme] = useState<string>('/r/');
  const [repCount, setRepCount] = useState<number>(4);
  const [drillTimerActive, setDrillTimerActive] = useState<boolean>(false);
  const [timerSeconds, setTimerSeconds] = useState<number>(10);
  const [activeDrillIndex, setActiveDrillIndex] = useState<number>(0);

  const guide = PHONEME_GUIDES[selectedPhoneme] || PHONEME_GUIDES['/r/'];
  const currentDrill = ORAL_MOTOR_DRILLS[activeDrillIndex];

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  useEffect(() => {
    let interval: any = null;
    if (drillTimerActive && timerSeconds > 0) {
      interval = setInterval(() => {
        setTimerSeconds(s => s - 1);
      }, 1000);
    } else if (timerSeconds === 0) {
      setDrillTimerActive(false);
      setRepCount(r => r + 1);
      setTimerSeconds(10);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [drillTimerActive, timerSeconds]);

  const startCamera = async () => {
    setCameraError(null);
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
          audio: false
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
          setCameraActive(true);
        }
      } else {
        setCameraError('Camera API is not supported in this browser. Running in anatomical simulation mode.');
      }
    } catch (err: any) {
      console.warn('Camera access error:', err);
      setCameraError('Camera access denied or unavailable. Running in visual anatomical guide mode.');
      setCameraActive(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
      setCameraActive(false);
    }
  };

  return (
    <div className="bg-[#1c1b1f] text-white p-5 sm:p-7 rounded-[32px] border border-white/10 shadow-2xl space-y-6">
      
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#ffd64e]/10 text-[#ffd64e] text-xs font-black rounded-full border border-[#ffd64e]/30 uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-[#ffd64e]" />
            <span>Digital Articulation Mirror & Oral-Motor Gym</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-white mt-1.5 flex items-center gap-2">
            <span>WebRTC Visual Articulation Studio</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              Hardware-Free
            </span>
          </h2>
          <p className="text-xs text-white/60">
            Replaces physical articulation mirror walls & motor kits with front-camera self-view and anatomical placement guides
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMirrorFlipped(!isMirrorFlipped)}
            className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold text-white transition flex items-center gap-1.5 cursor-pointer"
            title="Toggle Mirror Flip"
          >
            <RefreshCw className="w-3.5 h-3.5 text-[#ffd64e]" />
            <span>Flip Mirror</span>
          </button>

          {cameraActive ? (
            <button
              onClick={stopCamera}
              className="px-4 py-1.5 rounded-full bg-rose-600/80 hover:bg-rose-600 text-xs font-bold text-white transition flex items-center gap-1.5 cursor-pointer shadow-sm"
            >
              <CameraOff className="w-3.5 h-3.5" />
              <span>Pause Video</span>
            </button>
          ) : (
            <button
              onClick={startCamera}
              className="px-4 py-1.5 rounded-full bg-[#ffd64e] hover:bg-amber-400 text-black text-xs font-black transition flex items-center gap-1.5 cursor-pointer shadow-sm"
            >
              <Camera className="w-3.5 h-3.5" />
              <span>Start Camera</span>
            </button>
          )}
        </div>
      </div>

      {cameraError && (
        <div className="p-3 bg-amber-500/20 border border-amber-500/40 text-amber-200 rounded-2xl text-xs font-semibold flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
          <span>{cameraError}</span>
        </div>
      )}

      {/* Main 2-Pane Clinical Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Pane: Live WebRTC Mirror Stream (7 Cols) */}
        <div className="lg:col-span-7 space-y-4">
          <div className="relative aspect-4/3 w-full bg-black rounded-[28px] overflow-hidden border border-white/10 shadow-inner flex items-center justify-center">
            
            {/* Live Video Element */}
            <video
              ref={videoRef}
              playsInline
              muted
              className={`w-full h-full object-cover ${isMirrorFlipped ? 'scale-x-[-1]' : ''} ${!cameraActive ? 'hidden' : ''}`}
            />

            {/* Fallback Display if Camera is Disabled */}
            {!cameraActive && (
              <div className="p-8 text-center space-y-3">
                <div className="w-14 h-14 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto text-2xl">
                  🪞
                </div>
                <div className="text-sm font-black text-white">Digital Articulation Mirror</div>
                <p className="text-xs text-white/50 max-w-xs mx-auto">
                  Camera paused or simulated. Click "Start Camera" to observe student tongue and lip placement in real-time.
                </p>
                <button
                  onClick={startCamera}
                  className="px-5 py-2 rounded-full bg-[#ffd64e] hover:bg-amber-400 text-black text-xs font-black transition cursor-pointer shadow"
                >
                  Enable Front Camera Mirror
                </button>
              </div>
            )}

            {/* Visual Alignment Overlay Grid */}
            {cameraActive && (
              <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
                {/* Lip Alignment Center Box */}
                <div className="w-44 h-24 border-2 border-dashed border-[#ffd64e]/60 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-[10px] font-mono text-[#ffd64e] bg-black/60 px-2 py-0.5 rounded-full">
                    Mouth Center Target
                  </span>
                </div>
              </div>
            )}

            {/* Active Sound Badge */}
            <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-md px-3 py-1 rounded-full border border-white/15 text-xs font-black text-[#ffd64e] flex items-center gap-1.5">
              <span>Target Phoneme:</span>
              <span className="text-white font-mono text-sm">{guide.phoneme}</span>
            </div>
          </div>

          {/* Phoneme Target Selector Pills */}
          <div className="bg-white/5 p-3 rounded-2xl border border-white/10 flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-black uppercase text-white/60">Phonetic Target Drills:</span>
            <div className="flex flex-wrap items-center gap-1.5">
              {Object.keys(PHONEME_GUIDES).map((snd) => (
                <button
                  key={snd}
                  onClick={() => setSelectedPhoneme(snd)}
                  className={`px-3.5 py-1.5 rounded-xl font-black text-xs transition cursor-pointer ${
                    selectedPhoneme === snd
                      ? 'bg-[#ffd64e] text-black shadow-md scale-105'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  {snd}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Pane: Anatomical Cross-Section & Oral-Motor Gym (5 Cols) */}
        <div className="lg:col-span-5 space-y-4">
          
          {/* Anatomical Cross-Section Card */}
          <div className="p-5 rounded-[28px] bg-white/5 border border-white/10 space-y-3">
            <div className="flex items-center justify-between border-b border-white/10 pb-2">
              <span className="text-xs font-black text-[#ffd64e] uppercase tracking-wider">
                Anatomical Placement Target: {guide.phoneme}
              </span>
              <span className="text-[10px] text-white/50 font-mono">{guide.classification}</span>
            </div>

            {/* Target SVG Mouth Shape Illustration */}
            <div className="h-32 bg-slate-950 rounded-2xl border border-white/10 p-3 flex items-center justify-center relative overflow-hidden">
              <svg viewBox="0 0 100 100" className="w-28 h-28 text-[#ffd64e]">
                <circle cx="50" cy="50" r="46" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
                <path d={guide.targetMouthSvg} fill="rgba(255,214,78,0.25)" stroke="#ffd64e" strokeWidth="4" strokeLinecap="round" />
                <ellipse cx="50" cy="52" rx="14" ry="6" fill="#f87171" opacity="0.8" />
              </svg>
              <div className="absolute bottom-2 right-3 text-[10px] font-mono text-white/40">
                Lingua-Palatal Map
              </div>
            </div>

            <div className="space-y-2 text-xs">
              <div>
                <strong className="text-white block font-bold">Tongue Placement:</strong>
                <p className="text-white/70 leading-relaxed text-[11px]">{guide.tonguePlacement}</p>
              </div>
              <div>
                <strong className="text-white block font-bold">Lip & Jaw Shape:</strong>
                <p className="text-white/70 leading-relaxed text-[11px]">{guide.lipShape}</p>
              </div>
              <div className="p-2.5 rounded-xl bg-[#ffd64e]/10 border border-[#ffd64e]/20 text-[11px] text-[#ffd64e]">
                <strong>Therapist Cue:</strong> {guide.instructions}
              </div>
            </div>
          </div>

          {/* Oral-Motor Gym Section */}
          <div className="p-5 rounded-[28px] bg-white/5 border border-white/10 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Smile className="w-4 h-4 text-[#ffd64e]" />
                <span className="text-xs font-black text-white uppercase tracking-wider">
                  Oral-Motor Exercise Gym
                </span>
              </div>
              <span className="text-xs font-mono text-emerald-400 font-bold">
                {repCount} / {currentDrill.reps} Reps
              </span>
            </div>

            {/* Drill Selector Tabs */}
            <div className="grid grid-cols-3 gap-1.5">
              {ORAL_MOTOR_DRILLS.map((drill, idx) => (
                <button
                  key={drill.id}
                  onClick={() => { setActiveDrillIndex(idx); setTimerSeconds(10); setDrillTimerActive(false); }}
                  className={`p-2 rounded-xl text-[10px] font-black transition cursor-pointer border ${
                    activeDrillIndex === idx
                      ? 'bg-[#ffd64e] text-black border-[#ffd64e]'
                      : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'
                  }`}
                >
                  {drill.title}
                </button>
              ))}
            </div>

            {/* Active Drill Controls */}
            <div className="p-3 bg-black/40 rounded-2xl border border-white/5 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-white">{currentDrill.title}</div>
                <div className="text-[10px] text-white/50">{currentDrill.target}</div>
              </div>

              <div className="flex items-center gap-2">
                <div className="text-center px-2 font-mono text-xs text-[#ffd64e] font-bold">
                  {timerSeconds}s
                </div>
                <button
                  onClick={() => setDrillTimerActive(!drillTimerActive)}
                  className={`px-3 py-1.5 rounded-full text-xs font-black transition cursor-pointer flex items-center gap-1 ${
                    drillTimerActive ? 'bg-rose-600 text-white' : 'bg-emerald-500 text-black hover:bg-emerald-400'
                  }`}
                >
                  {drillTimerActive ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3 fill-current" />}
                  <span>{drillTimerActive ? 'Pause' : 'Start 10s'}</span>
                </button>
              </div>
            </div>

          </div>

        </div>

      </div>

    </div>
  );
};
